import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { CSVLink } from 'react-csv';
import { STag, SLoader, SIcon, SAlert, SSelect, SRowExpander } from '@avalara/skylab-sdk/react';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import LANGUAGES_CONTROLLER from '../../../controllers/languages';
import CLIENT from '../../../utils/restClient';
import { STATUS_ROW_TYPE } from '../../../utils/nlDigipoortV2Mock';
import Logger, { LOG_LEVELS } from '../../../utils/logger';
import { TableConfig } from '../../../utils/appConfig';

const log = new Logger('USER_TRANSACTION_HISTORY', LOG_LEVELS.DEBUG);

const STATE_TAG_COLOR = {
  success: 'green-lighter',
  info: 'blue-lighter',
  error: 'red-lighter',
};

const EXPORT_FILE_NAME = 'TransactionHistory.csv';

// Digipoort status codes: '500' is the final accepted state, '420' is a technical
// problem; both classifications mirror NLSubmissionFilingV2.getStatusRowType.
const DIGIPOORT_STATUS_CODE = Object.freeze({ Success: '500', TechnicalProblem: '420' });

function getHistoryRowStateType(row) {
  if (row.errorCode || `${row.statusCode}` === DIGIPOORT_STATUS_CODE.TechnicalProblem) {
    return STATUS_ROW_TYPE.Error;
  }
  if (`${row.statusCode}` === DIGIPOORT_STATUS_CODE.Success) {
    return STATUS_ROW_TYPE.Success;
  }
  return STATUS_ROW_TYPE.Info;
}

function mapUserSubmissionHistoryRows(rows) {
  return (rows || []).map((row, index) => ({
    id: index + 1,
    time: row.createdDatetime,
    title: row.documentName,
    state: `${row.statusCode}`,
    stateType: getHistoryRowStateType(row),
    kenmerk: row.kenmerk,
    errorCode: row.errorCode,
    errorDescription: row.errorDescription,
    statusDescription: row.statusDescription,
  }));
}

function getRefreshErrorMessage(languages) {
  const messages = [
    LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_RefreshFailed'),
    LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_HistoryFailed'),
  ];
  return messages.find((message) => message && !message.startsWith('MISSING LANGUAGE STRING')) || 'Unable to refresh transaction';
}

const DETAIL_VALUE_TRUNCATE_AT = 100;

function DetailRow({ labelKey, value, isError, isLast }) {
  const [expanded, setExpanded] = useState(false);
  const stringValue = value || '';
  const isLong = stringValue.length > DETAIL_VALUE_TRUNCATE_AT;
  const displayValue = isLong && !expanded ? `${stringValue.slice(0, DETAIL_VALUE_TRUNCATE_AT)}…` : stringValue;

  return (
    <div className={isLast ? 'nl-digipoort-v2-detail-row nl-digipoort-v2-detail-row-last' : 'nl-digipoort-v2-detail-row'}>
      <div className="bold"><LanguageStringResolver string={labelKey} />{':'}</div>
      <div style={isError ? { color: 'var(--color-status-error-fg)' } : undefined}>
        {stringValue ? displayValue : '—'}
        {isLong && (
          <a className="ml5" onClick={() => setExpanded((v) => !v)}>
            <LanguageStringResolver string={expanded ? 'submitFilingNLV2_Less' : 'submitFilingNLV2_More'} />
          </a>
        )}
      </div>
    </div>
  );
}

function UserTransactionHistory({ languages, digipoortUserName, digipoortPassword }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [refreshingIds, setRefreshingIds] = useState(() => new Set());
  const [historyErrorMessage, setHistoryErrorMessage] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(TableConfig.PAGINATION.STANDARD_LEVEL_TWO_PAGE_SIZE);

  useEffect(() => {
    CLIENT.getUserSubmissionHistory({
      digipoortUserName,
      digipoortPassword,
    })
      .then((response) => {
        setRows(mapUserSubmissionHistoryRows(response.rows));
        setPageIndex(0);
        setLoading(false);
      })
      .catch((error) => {
        log.debug('getUserSubmissionHistory error: ', error);
        setHistoryErrorMessage(LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_HistoryFailed'));
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const pagedRows = rows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  const onPageChange = (newPageIndex) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
  };

  const toggleExpanded = (id) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const onRefreshRow = (row) => {
    setRefreshingIds((current) => new Set(current).add(row.id));
    log.debug('refreshTransactionStatus for kenmerk', row.kenmerk);
    CLIENT.refreshTransactionStatus({
      transactionId: row.kenmerk,
      digipoortUserName: digipoortUserName,
      digipoortPassword: digipoortPassword,
    })
      .then((response) => {
        // map response into row shape used by this component
        const updated = Object.assign({}, row, {
          time: response.timestamp || row.time,
          state: response.statusCode ? `${response.statusCode}` : row.state,
          stateType: getHistoryRowStateType({ statusCode: response.statusCode, errorCode: response.errorCode }),
          errorCode: response.errorCode || '',
          errorDescription: response.errorDescription || '',
          statusDescription: response.statusDescription || row.statusDescription,
          conditions: response.conditions || '',
        });
        setRows((current) => current.map((r) => (r.id === row.id ? updated : r)));
        setRefreshingIds((current) => {
          const next = new Set(current);
          next.delete(row.id);
          return next;
        });
      })
      .catch((err) => {
        log.debug('refreshTransactionStatus error: ', err);
        setHistoryErrorMessage(getRefreshErrorMessage(languages));
        setRefreshingIds((current) => {
          const next = new Set(current);
          next.delete(row.id);
          return next;
        });
      });
  };

  const exportHeaders = [
    { key: 'id', label: '#' },
    { key: 'time', label: LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_ColumnTimeOfTransmission') },
    { key: 'title', label: LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_ColumnDocumentTitle') },
    { key: 'state', label: LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_ColumnCurrentState') },
    { key: 'errorCode', label: LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_ColumnErrorCode') },
    { key: 'statusDescription', label: LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_CurrentStatusDescription') },
  ];

  if (loading) {
    return (
      <div className="mt10 mb10" style={{ display: 'flex', justifyContent: 'center' }} data-testid="nl-digipoort-v2-transaction-history-loading">
        <SLoader loading className="small" />
      </div>
    );
  }

  return (
    <div data-testid="nl-digipoort-v2-transaction-history">
      <h4 className="mt10 mb10">
        <SIcon name="folder" className="mr5" style={{ verticalAlign: 'middle' }} />
        <LanguageStringResolver string="submitFilingNLV2_HistoryOverview" />
      </h4>
      {historyErrorMessage && (
        <SAlert status="error" className="mb10" noDismiss>
          <span>{historyErrorMessage}</span>
        </SAlert>
      )}
      <table className="updated-card-table">
        <thead>
          <tr>
            <th className="nl-digipoort-v2-expander-column"></th>
            <th style={{ textAlign: 'left' }}>#</th>
            <th><LanguageStringResolver string="submitFilingNLV2_ColumnTimeOfTransmission" /></th>
            <th><LanguageStringResolver string="submitFilingNLV2_ColumnDocumentTitle" /></th>
            <th><LanguageStringResolver string="submitFilingNLV2_ColumnCurrentState" /></th>
            <th className="nl-digipoort-v2-actions-column"><LanguageStringResolver string="submitFilingNLV2_ColumnRefreshTransaction" /></th>
          </tr>
        </thead>
        <tbody>
          {pagedRows.map((row) => {
            const isExpanded = expandedIds.has(row.id);
            const isRefreshing = refreshingIds.has(row.id);

            return (
              <React.Fragment key={row.id}>
                <tr>
                  <td className="nl-digipoort-v2-expander-column">
                    <SRowExpander
                      expanded={isExpanded}
                      onClick={() => toggleExpanded(row.id)}
                      data-testid={`nl-digipoort-v2-expander-row-${row.id}`}
                    />
                  </td>
                  <td style={{ textAlign: 'left' }}>{row.id}</td>
                  <td>{row.time}</td>
                  <td>{row.title}</td>
                  <td><STag color={STATE_TAG_COLOR[row.stateType]}><span slot="content">{row.state}</span></STag></td>
                  <td className="nl-digipoort-v2-actions-column">
                    <button
                      type="button"
                      className={isRefreshing ? 'small ghost icon disabled nl-digipoort-v2-row-refresh' : 'small ghost icon nl-digipoort-v2-row-refresh'}
                      aria-label={LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_Refresh')}
                      data-testid={`nl-digipoort-v2-refresh-row-${row.id}`}
                      onClick={isRefreshing ? undefined : () => onRefreshRow(row)}
                    >
                      <SIcon name="refresh" className={isRefreshing ? 'nl-digipoort-v2-spin' : ''} />
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="row-collapse">
                    <td colSpan={6}>
                      <div className="nl-digipoort-v2-history-details">
                        <DetailRow labelKey="submitFilingNLV2_Kenmerk" value={row.kenmerk} />
                        <DetailRow labelKey="submitFilingNLV2_Conditions" value={row.conditions} />
                        <DetailRow labelKey="submitFilingNLV2_ColumnErrorCode" value={row.errorCode} isError={!!row.errorCode} />
                        <DetailRow labelKey="submitFilingNLV2_ColumnErrorDescription" value={row.errorDescription} isError={!!row.errorDescription} />
                        <DetailRow labelKey="submitFilingNLV2_CurrentStatusDescription" value={row.statusDescription} isError={row.stateType === 'error'} isLast />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {rows.length > 0 && (
        <div className="mt10 mb10 nl-digipoort-v2-pagination">
          <div className="nl-digipoort-v2-pagination-controls">
            <a
              className={pageIndex === 0 ? 'button secondary icon-leading disabled' : 'button secondary icon-leading'}
              onClick={pageIndex === 0 ? undefined : () => onPageChange(pageIndex - 1)}
            >
              <SIcon name="chevron-left" />
              <LanguageStringResolver string="submitFilingNLV2_PreviousPage" />
            </a>
            <span className="nl-digipoort-v2-pagination-info">
              <LanguageStringResolver string="submitFilingNLV2_PageOf" params={[pageIndex + 1, pageCount]} />
            </span>
            <a
              className={pageIndex >= pageCount - 1 ? 'button secondary icon-trailing disabled' : 'button secondary icon-trailing'}
              onClick={pageIndex >= pageCount - 1 ? undefined : () => onPageChange(pageIndex + 1)}
            >
              <LanguageStringResolver string="submitFilingNLV2_NextPage" />
              <SIcon name="chevron-right" />
            </a>
          </div>
          <span className="nl-digipoort-v2-pagination-size">
            <SSelect
              inputId="nl-digipoort-v2-history-page-size"
              optionsList={TableConfig.PAGINATION.STANDARD_PAGE_SIZE_OPTIONS.map((option) => ({
                key: option.toString(),
                value: option.toString(),
                label: LANGUAGES_CONTROLLER.format(languages, 'submitFilingNLV2_RowsPerPage', [option]),
                selected: pageSize === option,
              }))}
              multiple={false}
              onS-select={(e) => onPageSizeChange(Number(e.detail.item.value))}
            />
          </span>
        </div>
      )}

      <div className="mt20">
        <CSVLink data={rows} headers={exportHeaders} filename={EXPORT_FILE_NAME} className="button primary">
          <SIcon name="download" className="mr5" style={{ verticalAlign: 'middle' }} />
          <LanguageStringResolver string="submitFilingNLV2_ExportTransactionHistory" />
        </CSVLink>
      </div>
    </div>
  );
}


function mapStateToProps(state) {
  return {
    languages: state.languages,
  };
}

export default connect(mapStateToProps)(UserTransactionHistory);
